import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import SummaryApi from '../common/index';
import { FaSearch } from "react-icons/fa";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";

const AllPlans = () => {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [expandedPlans, setExpandedPlans] = useState({});

  useEffect(() => {
    fetchAllPlans();
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modalOpen]);

  const fetchAllPlans = async () => {
    try {
      const response = await fetch(SummaryApi.getAllPlan.url, {
        method: SummaryApi.getAllPlan.method,
        headers: { 'content-type': 'application/json' },
      });

      const result = await response.json();

      if (result.status === true && Array.isArray(result.data)) {
        setPlans(result.data);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans([]);
    }
  };

  const filtered = Array.isArray(plans)
    ? plans.filter((p) =>
        (p.plan_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const openCreateModal = () => {
    setEditPlan(null);
    setModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditPlan(plan);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditPlan(null);
  };

  const toggleExpand = (planId) => {
    setExpandedPlans((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));
  };

  const showSuccessToast = useCallback((title, description) => {
    if (!title || !description) return;
    toast.custom(
      () => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-rose-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
                <CheckCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-rose-950">{title}</p>
                <p className="text-xs text-rose-900/60 mt-1">{description}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss("plan-success")}
            className="px-4 font-bold text-rose-600"
          >
            Close
          </button>
        </motion.div>
      ),
      { id: "plan-success", duration: 500 }
    );
  }, []);

  const handleSave = async (data) => {
    const finalData = {
      ...data,
      is_active: data.is_active ? 1 : 0,
    };

    const url = editPlan ? SummaryApi.UpdatePlan.url : SummaryApi.CreatePlan.url;

    try {
      const res = await fetch(url, {
        method: editPlan ? SummaryApi.UpdatePlan.method : SummaryApi.CreatePlan.method,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(finalData),
      });

      const resData = await res.json();
      if (resData.status === true) {
        const name = editPlan ? editPlan.plan_name : data.plan_name || "New Plan";

        showSuccessToast(
          "Success!",
          editPlan
            ? `Plan "${name}" updated successfully.`
            : `Plan "${name}" created successfully.`
        );

        fetchAllPlans();
        closeModal();
      } else {
        toast.error(resData.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Network error. Please try again.');
    }
  };

  const PlanModal = () => {
    const isEditMode = !!editPlan;

    const [formData, setFormData] = useState(
      editPlan
        ? { ...editPlan, is_active: editPlan.is_active === 1 }
        : { plan_name: '', duration: '', description: '', is_active: true }
    );

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    };

    const onSubmit = (e) => {
      e.preventDefault();
      if (editPlan?.plan_id) formData.plan_id = editPlan.plan_id;
      handleSave(formData);
    };

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center pt-10 px-4 overflow-y-auto"
        onClick={closeModal}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto relative scrollbar scrollbar-thumb-slate-500 scrollbar-track-gray-100 scrollbar-w-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-30 bg-white border-b px-6 py-2 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">
              {isEditMode ? 'Edit Plan' : 'Create Plan'}
            </h2>
            <button onClick={closeModal} className="rounded-full p-2 hover:bg-gray-100 transition">
              <FaTimes className="text-2xl text-gray-600" />
            </button>
          </div>

          <form onSubmit={onSubmit} className="p-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name {!isEditMode && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  name="plan_name"
                  value={formData.plan_name}
                  onChange={handleChange}
                  required={!isEditMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration {!isEditMode && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required={!isEditMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                required={!isEditMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center cursor-pointer select-none gap-3">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition ${
                    formData.is_active ? 'bg-pink-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </div>
                <span className="font-medium text-gray-700">Active</span>
              </label>
            </div>

            <div className="flex justify-end pt-6 border-t">
              <button
                type="submit"
                className="px-8 py-3 bg-pink-600 text-white font-bold rounded-lg hover:bg-pink-700 transition shadow-md"
              >
                {isEditMode ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };


const PinkPlanCard = ({ plan }) => {
  const isExpanded = expandedPlans[plan.plan_id] || false;
  const description = (plan.description || '').trim();

  // Decide if we should show Read More button at all
  const shouldShowReadMore = description.length > 220 || description.split('\n').length > 4;

  return (
    <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col h-full">
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-gray-900 line-clamp-2 pr-3">
            {plan.plan_name}
          </h3>
          <span
            className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
              plan.is_active === 1
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {plan.is_active === 1 ? 'Active' : 'Inactive'}
          </span>
        </div>

        <p className="text-gray-600 text-sm mb-5">
          Duration: <span className="font-medium">{plan.duration}</span>
        </p>

        {/* ──────────────────────────────────────────────── */}
        {/*               DESCRIPTION AREA                   */}
        {/* ──────────────────────────────────────────────── */}
    <div className="text-gray-700 text-[15px] leading-relaxed">
  <div
    className={`
      h-[140px]               /* FIXED HEIGHT */
      transition-all duration-300 ease-in-out
      ${isExpanded ? 'overflow-y-auto' : 'overflow-y-hidden'}
      whitespace-pre-wrap break-words
      scrollbar-thin scrollbar-thumb-pink-400 scrollbar-track-transparent
    `}
  >
    {description}
  </div>

  {shouldShowReadMore && (
    <button
      type="button"
      onClick={() => toggleExpand(plan.plan_id)}
      className="mt-3 inline-block text-pink-600 hover:text-pink-700 font-medium text-sm transition-colors"
    >
      {isExpanded ? 'Read Less ▲' : 'Read More ▼'}
    </button>
  )}
</div>

        <div className="mt-8">
          <button
            onClick={() => openEditModal(plan)}
            className="w-full py-3 bg-pink-600 text-white font-semibold rounded-xl hover:bg-pink-700 transition shadow-md active:scale-[0.98]"
          >
            Edit Plan
          </button>
        </div>
      </div>
    </div>
  );
};

  return (
    <>
      <Toaster position="top-center" toastOptions={{ duration: 2000 }} />

      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="flex justify-between items-center mb-6 px-6 py-2">
          <h1 className="text-3xl font-extrabold text-pink-800">All Plans</h1>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-6 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-lg shadow-lg transition"
          >
            <FaPlus /> Create Plan
          </button>
        </div>

        <div className="px-6 mb-6">
          <div className="relative w-full max-w-md">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by plan name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
            />
          </div>
        </div>

        <div className="px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-500 text-lg">
              {search ? 'No plans match your search.' : 'No plans available yet.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((plan) => (
                <PinkPlanCard key={plan.plan_id} plan={plan} />
              ))}
            </div>
          )}
        </div>

        {modalOpen && <PlanModal />}
      </div>
    </>
  );
};

export default AllPlans;